import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Tabs,
  Tab,
  Slider,
  Divider,
} from '@mui/material';
import { useStyleTemplate } from '../contexts/StyleTemplateContext';
import { useNotification } from '../contexts/NotificationContext';
import vsaasTheme, { PALETTE_IDS, PALETTE_LABELS, DEFAULT_HEATMAP } from '../theme';

const DEFAULT_SITE_THEME = { primary: vsaasTheme.primary.main, secondary: vsaasTheme.secondary.main };

const StyleTemplateEditor = ({ open, onClose, embedded = false }) => {
  const { showSuccess, showError } = useNotification();
  const {
    palette,
    palettes,
    activeId,
    setActiveId,
    defaultPalette,
    updateColorAt,
    updateHeatmapSetting,
    resetPalette,
    savePalette,
    heatmap,
    defaultHeatmap,
    siteLogo,
    reportLogo,
    siteLogoData,
    reportLogoData,
    siteTheme,
    setSiteTheme,
  } = useStyleTemplate();
  const [local, setLocal] = useState([]);
  const [saving, setSaving] = useState(false);
  const [localHeatmap, setLocalHeatmap] = useState({ ...DEFAULT_HEATMAP });
  const [localSiteLogoData, setLocalSiteLogoData] = useState('');
  const [localReportLogoData, setLocalReportLogoData] = useState('');
  const [localSiteTheme, setLocalSiteTheme] = useState({ ...DEFAULT_SITE_THEME });

  useEffect(() => {
    if (open && palette?.colors) {
      setLocal(palette.colors.map((c) => c));
    }
  }, [open, palette?.colors]);

  const handleTabChange = (_, value) => {
    if (PALETTE_IDS.includes(value)) setActiveId(value);
  };

  useEffect(() => {
    if (open && palettes?.[activeId]?.colors) {
      setLocal(palettes[activeId].colors.map((c) => c));
    }
  }, [open, activeId, palettes]);

  useEffect(() => {
    if (open) {
      setLocalSiteLogoData(siteLogoData ?? '');
      setLocalReportLogoData(reportLogoData ?? '');
      setLocalSiteTheme(siteTheme && typeof siteTheme === 'object' ? { ...DEFAULT_SITE_THEME, ...siteTheme } : { ...DEFAULT_SITE_THEME });
    }
  }, [open, siteLogoData, reportLogoData, siteTheme]);

  const handleColorChange = (index, hex) => {
    const norm = hex.startsWith('#') ? hex : `#${hex}`;
    setLocal((prev) => {
      const next = [...(prev || [])];
      next[index] = norm;
      return next;
    });
    updateColorAt(index, norm);
  };

  const handleReset = () => {
    resetPalette();
    setLocal([...(defaultPalette?.colors || [])]);
    setLocalHeatmap({ ...DEFAULT_HEATMAP });
  };

  const handleHeatmapChange = (field, value) => {
    setLocalHeatmap((prev) => ({ ...prev, [field]: value }));
    updateHeatmapSetting(field, value);
  };

  const handleLogoFile = (setter, e) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setter(reader.result || '');
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!savePalette) return;
    setSaving(true);
    try {
      await savePalette({
        siteLogoData: localSiteLogoData,
        reportLogoData: localReportLogoData,
        siteTheme: localSiteTheme,
      });
      setSiteTheme?.(localSiteTheme);
      showSuccess('Apariencia guardada');
      if (!embedded && onClose) onClose();
    } catch (e) {
      console.warn('Error al guardar:', e);
      showError('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const cur = palettes?.[activeId] || palette;
  const def = defaultPalette || {};
  const getColor = (i) =>
    local[i] ?? cur?.colors?.[i] ?? def.colors?.[i] ?? vsaasTheme.chart.colors[i] ?? vsaasTheme.primary.main;
  const n = Math.max(
    local.length,
    cur?.colors?.length ?? 0,
    def.colors?.length ?? 0,
    10
  );

  const content = (
    <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Colores del sitio, paletas de gráficos y logos. Se guardan en la base de datos y aplican a todos los usuarios.
        </Typography>

        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Colores del sitio (barra y tema)</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Color primario (barra, botones)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <input
                id="site-color-primary"
                name="siteColorPrimary"
                type="color"
                aria-label="Color primario del sitio"
                value={(localSiteTheme.primary || DEFAULT_SITE_THEME.primary).slice(0, 7)}
                onChange={(e) => setLocalSiteTheme((p) => ({ ...p, primary: e.target.value }))}
                style={{ width: 40, height: 36, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
              />
              <TextField
                size="small"
                value={(localSiteTheme.primary || '').replace(/^#/, '')}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                  setLocalSiteTheme((p) => ({ ...p, primary: v ? `#${v}` : DEFAULT_SITE_THEME.primary }));
                }}
                placeholder="hex"
                sx={{ width: 90 }}
                inputProps={{ maxLength: 6, 'aria-label': 'Color primario del sitio (hexadecimal)' }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Color secundario</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <input
                id="site-color-secondary"
                name="siteColorSecondary"
                type="color"
                aria-label="Color secundario del sitio"
                value={(localSiteTheme.secondary || DEFAULT_SITE_THEME.secondary).slice(0, 7)}
                onChange={(e) => setLocalSiteTheme((p) => ({ ...p, secondary: e.target.value }))}
                style={{ width: 40, height: 36, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
              />
              <TextField
                size="small"
                value={(localSiteTheme.secondary || '').replace(/^#/, '')}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                  setLocalSiteTheme((p) => ({ ...p, secondary: v ? `#${v}` : DEFAULT_SITE_THEME.secondary }));
                }}
                placeholder="hex"
                sx={{ width: 90 }}
                inputProps={{ maxLength: 6, 'aria-label': 'Color secundario del sitio (hexadecimal)' }}
              />
            </Box>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Logos (subir archivo, se guarda en la BD)</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" display="block">Logo del sitio (header)</Typography>
            <input
              id="site-logo-file"
              name="siteLogoFile"
              accept="image/*"
              type="file"
              aria-label="Logo del sitio"
              onChange={(e) => handleLogoFile(setLocalSiteLogoData, e)}
              style={{ marginTop: 4 }}
            />
            {localSiteLogoData && (
              <Box sx={{ mt: 1 }}>
                <img src={localSiteLogoData} alt="Vista previa logo sitio" style={{ maxHeight: 48, objectFit: 'contain' }} />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" display="block">Logo de reportes PDF</Typography>
            <input
              id="report-logo-file"
              name="reportLogoFile"
              accept="image/*"
              type="file"
              aria-label="Logo de reportes PDF"
              onChange={(e) => handleLogoFile(setLocalReportLogoData, e)}
              style={{ marginTop: 4 }}
            />
            {localReportLogoData && (
              <Box sx={{ mt: 1 }}>
                <img src={localReportLogoData} alt="Vista previa logo reportes" style={{ maxWidth: 200, maxHeight: 48, objectFit: 'contain' }} />
              </Box>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Paleta de colores (gráficos)</Typography>
        <Tabs value={activeId} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          {PALETTE_IDS.map((id) => (
            <Tab key={id} label={PALETTE_LABELS[id] || id} value={id} />
          ))}
        </Tabs>
        <Grid container spacing={2}>
          {Array.from({ length: n }, (_, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  id={`palette-color-${i}`}
                  name={`paletteColor${i}`}
                  type="color"
                  aria-label={`Color de paleta #${i + 1}`}
                  value={getColor(i).slice(0, 7)}
                  onChange={(e) => handleColorChange(i, e.target.value)}
                  style={{
                    width: 40,
                    height: 36,
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    cursor: 'pointer',
                    padding: 0,
                  }}
                />
                <TextField
                  size="small"
                  value={getColor(i).replace(/^#/, '')}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                    const hex = v ? `#${v}` : getColor(i);
                    setLocal((prev) => {
                      const next = [...(prev || [])];
                      next[i] = hex;
                      return next;
                    });
                    if (v.length === 6) updateColorAt(i, hex);
                  }}
                  placeholder="hex"
                  sx={{ width: 90 }}
                  inputProps={{ maxLength: 6, 'aria-label': `Color de paleta #${i + 1} (hexadecimal)` }}
                />
                <Typography variant="caption" color="text.secondary">
                  #{i + 1}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" gutterBottom>Propiedades de heatmap</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Transparencia y colores del rango (bajo → alto) para el heatmap sobre imagen. Afectan solo a ese tipo de gráfico.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Transparencia de celdas (0 = opaco, 1 = muy transparente)</Typography>
            <Slider
              size="small"
              value={localHeatmap.cellOpacity ?? DEFAULT_HEATMAP.cellOpacity}
              min={0}
              max={1}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              onChange={(_, v) => handleHeatmapChange('cellOpacity', v)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Color bajo (valores mínimos)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <input
                id="heatmap-color-low"
                name="heatmapColorLow"
                type="color"
                aria-label="Color bajo del heatmap"
                value={(localHeatmap.colorLow || DEFAULT_HEATMAP.colorLow).slice(0, 7)}
                onChange={(e) => handleHeatmapChange('colorLow', e.target.value)}
                style={{ width: 40, height: 36, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
              />
              <TextField
                size="small"
                value={(localHeatmap.colorLow || DEFAULT_HEATMAP.colorLow).replace(/^#/, '')}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                  if (v.length === 6) handleHeatmapChange('colorLow', `#${v}`);
                }}
                placeholder="hex"
                sx={{ width: 90 }}
                inputProps={{ maxLength: 6, 'aria-label': 'Color bajo del heatmap (hexadecimal)' }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">Color alto (valores máximos)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <input
                id="heatmap-color-high"
                name="heatmapColorHigh"
                type="color"
                aria-label="Color alto del heatmap"
                value={(localHeatmap.colorHigh || DEFAULT_HEATMAP.colorHigh).slice(0, 7)}
                onChange={(e) => handleHeatmapChange('colorHigh', e.target.value)}
                style={{ width: 40, height: 36, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
              />
              <TextField
                size="small"
                value={(localHeatmap.colorHigh || DEFAULT_HEATMAP.colorHigh).replace(/^#/, '')}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                  if (v.length === 6) handleHeatmapChange('colorHigh', `#${v}`);
                }}
                placeholder="hex"
                sx={{ width: 90 }}
                inputProps={{ maxLength: 6, 'aria-label': 'Color alto del heatmap (hexadecimal)' }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Transparencia del color de rango</Typography>
            <Slider
              size="small"
              value={localHeatmap.colorOpacity ?? DEFAULT_HEATMAP.colorOpacity}
              min={0}
              max={1}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              onChange={(_, v) => handleHeatmapChange('colorOpacity', v)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
        </Grid>
      </>
  );

  const actions = (
    <>
      <Button onClick={handleReset} color="inherit">
        Restaurar &quot;{PALETTE_LABELS[activeId] || activeId}&quot; por defecto
      </Button>
      {!embedded && <Button onClick={onClose}>Cerrar</Button>}
      <Button onClick={handleSave} variant="contained" disabled={!savePalette || saving}>
        {saving ? 'Guardando…' : 'Guardar'}
      </Button>
    </>
  );

  if (embedded) {
    return (
      <Box sx={{ p: 0 }}>
        {content}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>{actions}</Box>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editor de paleta y logos (solo admin)</DialogTitle>
      <DialogContent dividers>{content}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
};

export default StyleTemplateEditor;
